import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useLearnerContext } from "@/lib/learner-context";
import { supabase } from "@/integrations/supabase/client";
import { BookOpen, Loader2, User, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/learner/subjects")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Subjects — MAKAI" }] }),
  component: SubjectsPage,
});

type Row = {
  id: string;
  subject: { id: string; name: string; code: string | null; is_core: boolean | null } | null;
  teachers: string[];
  totalLessons: number;
  doneLessons: number;
};

function SubjectsPage() {
  const { ctx, loading } = useLearnerContext();
  const [rows, setRows] = useState<Row[]>([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!ctx?.classId) return;
    const classId = ctx.classId;
    const streamId = ctx.streamId;
    setFetching(true);
    (async () => {
      const { data: cs } = await supabase
        .from("class_subjects")
        .select("id, subject:subjects_catalog(id, name, code, is_core)")
        .eq("class_id", classId);
      const list = (cs ?? []) as { id: string; subject: { id: string; name: string; code: string | null; is_core: boolean | null } | null }[];

      // Fetch all my completed lessons once, count by subject locally
      const { data: doneRows } = await supabase
        .from("lesson_completions")
        .select("lesson:lessons!inner(subject_id)")
        .eq("learner_id", ctx.learnerId).eq("status", "completed");
      const doneBySubject: Record<string, number> = {};
      ((doneRows ?? []) as { lesson: { subject_id: string | null } | null }[]).forEach((r) => {
        const sid = r.lesson?.subject_id; if (sid) doneBySubject[sid] = (doneBySubject[sid] ?? 0) + 1;
      });

      const enriched: Row[] = await Promise.all(list.map(async (cs) => {
        if (!cs.subject) return { id: cs.id, subject: null, teachers: [], totalLessons: 0, doneLessons: 0 };
        const [teachers, lessons] = await Promise.all([
          streamId ? supabase.from("staff_assignments")
            .select("staff:staff(first_name, last_name)")
            .eq("stream_id", streamId).eq("subject_id", cs.subject.id) : Promise.resolve({ data: [] }),
          supabase.from("lessons").select("id", { count: "exact", head: true })
            .eq("school_id", ctx.schoolId).eq("subject_id", cs.subject.id).eq("published", true),
        ]);
        const tnames = ((teachers.data ?? []) as { staff: { first_name: string | null; last_name: string | null } | null }[])
          .map((t) => `${t.staff?.first_name ?? ""} ${t.staff?.last_name ?? ""}`.trim())
          .filter(Boolean);
        return {
          id: cs.id,
          subject: cs.subject,
          teachers: tnames,
          totalLessons: lessons.count ?? 0,
          doneLessons: doneBySubject[cs.subject.id] ?? 0,
        };
      }));
      setRows(enriched);
      setFetching(false);
    })();
  }, [ctx]);

  if (loading) return <AppShell role="learner"><Spinner /></AppShell>;

  return (
    <AppShell role="learner" title="My Subjects" subtitle={ctx?.gradeLevel ? `${ctx.gradeLevel} curriculum` : "Your enrolled subjects"}>
      {fetching && <Spinner />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.length === 0 && !fetching && (
          <div className="col-span-full rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No subjects assigned to your class yet.
          </div>
        )}
        {rows.map((r) => r.subject && (
          <Link
            key={r.id}
            to="/learner/lessons"
            search={{ subject: r.subject.id } as never}
            className="group rounded-2xl border border-border bg-card p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-card"
          >
            <div className="flex items-center justify-between">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary-soft text-primary">
                <BookOpen className="h-6 w-6" />
              </div>
              {r.subject.is_core && <span className="rounded-md bg-gold/20 px-2 py-0.5 text-[10px] font-semibold text-gold-foreground">Core</span>}
            </div>
            <h3 className="mt-4 font-display text-lg font-bold">{r.subject.name}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{r.subject.code ?? ""}</p>

            {r.teachers.length > 0 && (
              <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                <User className="h-3.5 w-3.5" /> <span className="truncate">{r.teachers.join(", ")}</span>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{r.doneLessons}/{r.totalLessons} lessons</span>
              <span className="inline-flex items-center gap-1 font-semibold text-primary">
                Open <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
              </span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary" style={{ width: `${r.totalLessons ? (r.doneLessons / r.totalLessons) * 100 : 0}%` }} />
            </div>
          </Link>
        ))}
      </div>
    </AppShell>
  );
}

function Spinner() {
  return <div className="grid place-items-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
}
